import { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { Quote, UserCircle2 } from 'lucide-react';

export default function Testimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const q = query(collection(db, 'testimonials'), orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(q);
        const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // Add fake fallback data if database is empty to let admin preview the layout
        if (data.length === 0) {
          setTestimonials([
            { id: '1', name: 'أحمد محمد', review: 'دورة ممتازة جداً غيرت مستواي في التصميم بالكامل بفضل الله ثم بفضل الأستاذ اصيل.', image: '' },
            { id: '2', name: 'سارة خالد', review: 'شرح واضح ومبسط التطبيق العملي هو أفضل شيء في هذه الدورة.', image: '' },
            { id: '3', name: 'عمر ياسر', review: 'أنصح أي شخص مبتدئ في مجال التصميم أن ينضم لدورة لباب بدون تردد.', image: '' },
          ]);
        } else {
          setTestimonials(data);
        }
      } catch (error) {
        console.error("Error fetching testimonials:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchTestimonials();
  }, []);

  if (loading) return null;

  return (
    <section id="testimonials" className="py-16 sm:py-24 bg-white dark:bg-zinc-950 relative overflow-hidden flex flex-col items-center border-y border-slate-100 dark:border-white/5" dir="rtl">
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(50%); }
        }
        .animate-marquee {
          animation: marquee 100s linear infinite;
        }
        .animate-marquee:hover {
          animation-play-state: paused;
        }
      `}</style>

      {/* Background Orbs */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary-500/5 dark:bg-primary-500/10 rounded-full blur-[100px] pointer-events-none -mt-20"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary-500/5 dark:bg-primary-500/10 rounded-full blur-[100px] pointer-events-none -mb-20"></div>

      <div className="container mx-auto px-4 text-center mb-10 sm:mb-16 relative z-10">
        <h2 className="text-2xl sm:text-3xl md:text-5xl font-extrabold mb-4 sm:mb-6 text-slate-900 dark:text-white">
          قصص نجاح <span className="text-transparent bg-clip-text bg-gradient-to-l from-primary-600 to-primary-400">طلابنا</span>
        </h2>
        <p className="text-base sm:text-lg md:text-xl text-slate-600 dark:text-zinc-400 max-w-2xl mx-auto px-2 sm:px-0">
          انضم إلى المئات من المصممين الذين غيروا مسارهم المهني من خلال دورات لباب سكول.
        </p>
      </div>

      {/* Marquee Wrapper */}
      <div className="w-full relative flex overflow-hidden group py-4">
        {/* Gradients for smooth fading edges */}
        <div className="absolute top-0 bottom-0 right-0 w-24 md:w-32 bg-gradient-to-l from-white dark:from-zinc-950 to-transparent z-20 pointer-events-none"></div>
        <div className="absolute top-0 bottom-0 left-0 w-24 md:w-32 bg-gradient-to-r from-white dark:from-zinc-950 to-transparent z-20 pointer-events-none"></div>

        <div className="flex animate-marquee w-max gap-6 pr-6">
          {/* We render 4 identical sets for infinite loop safety */}
          {[1, 2, 3, 4].map((set) => (
            <div key={`set-${set}`} className="flex gap-6 shrink-0">
              {testimonials.map((item, idx) => (
                <div
                  key={`item-${set}-${item.id}-${idx}`}
                  className="w-[260px] sm:w-[300px] md:w-[400px] p-5 sm:p-8 rounded-2xl sm:rounded-3xl bg-slate-50 dark:bg-zinc-900/40 border border-slate-200 dark:border-white/10 shadow-lg flex flex-col gap-4 sm:gap-6 relative group/card hover:-translate-y-2 transition-transform duration-300 backdrop-blur-xl"
                  dir="rtl"
                >
                  <div className="flex items-center gap-4 mt-auto z-10">
                    {item.image ? (
                      <img src={item.image} alt={item.name} className="w-14 h-14 rounded-full object-cover border-2 border-white dark:border-zinc-800 shadow-md" />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-primary-100 dark:bg-primary-900/50 flex items-center justify-center border-2 border-white dark:border-zinc-800 shadow-md">
                        <UserCircle2 className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                      </div>
                    )}
                    <div>
                      <h3 className="font-extrabold text-slate-900 dark:text-white">{item.name}</h3>
                      <span className="text-mdtext-primary-600 dark:text-primary-400 font-bold">  احد مبدعي لباب سكول</span>
                    </div>
                  </div>
                  <Quote className="absolute top-6 left-6 w-12 h-12 text-primary-500/10 dark:text-primary-500/20 group-hover/card:scale-110 transition-transform" />
                  <p className="text-base sm:text-lg text-slate-700 dark:text-zinc-300 leading-relaxed font-medium z-10">
                    "{item.review}"
                  </p>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
